import * as React from "react";
import { cn } from "@/lib/utils";

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title: React.ReactNode;
  badge?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  headingLevel?: "h1" | "h2" | "h3";
}

/**
 * PageHeader enforces a single canonical heading per page or view section.
 * Includes optional badge, description, and actions slot.
 */
export function PageHeader({
  title,
  badge,
  description,
  actions,
  headingLevel: Heading = "h1",
  className,
  ...props
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-3">
          <Heading className="text-2xl font-semibold tracking-tight text-foreground">
            {title}
          </Heading>
          {badge}
        </div>
        {description ? (
          <div className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {description}
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}
